import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

const DEFAULT_MODE = 'count';

const ensureMinimumCount = (value) => {
    const parsed = parseInt(value, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
        return 1;
    }
    return Math.min(parsed, 500);
};

const getCsrfToken = () => {
    if (typeof document === 'undefined') {
        return '';
    }
    const tokenTag = document.querySelector('meta[name="csrf-token"]');
    return tokenTag ? tokenTag.getAttribute('content') ?? '' : '';
};

const sanitizeDeviceId = (value) => {
    if (value === null || typeof value === 'undefined' || value === '') {
        return '';
    }
    const stringValue = String(value);
    if (stringValue.startsWith('__header-')) {
        return '';
    }
    return stringValue;
};

const buildDeviceLookup = (devices) => {
    const map = new Map();
    devices.forEach((group) => {
        const { label, devices: groupDevices = [] } = group;
        groupDevices.forEach((device) => {
            map.set(String(device.id), { ...device, groupLabel: label });
        });
    });
    return map;
};

const CloneManyModal = ({ urlTemplate, devices }) => {
    const normalizedDevices = useMemo(() => (Array.isArray(devices) ? devices : []), [devices]);
    const fileInputRef = useRef(null);
    const [state, setState] = useState({
        isOpen: false,
        mode: DEFAULT_MODE,
        count: 1,
        csvFile: null,
        actionUrl: '',
        selectedDevice: '',
        currentDeviceId: null,
        currentDeviceGroup: null,
        isSubmitting: false,
        result: null,
        formError: null,
        itemName: '',
    });

    const deviceLookup = useMemo(() => buildDeviceLookup(normalizedDevices), [normalizedDevices]);
    const csrfToken = useMemo(() => getCsrfToken(), []);

    useEffect(() => {
        const handleOpen = (event) => {
            const detail = event.detail || {};
            const itemId = detail.itemId ? String(detail.itemId) : null;
            if (!itemId) {
                return;
            }

            const resolvedUrl = typeof urlTemplate === 'string' ? urlTemplate.replace('item:', itemId) : '';
            const sanitizedDeviceId = sanitizeDeviceId(detail.deviceId);
            const currentDeviceId = sanitizedDeviceId || null;
            const currentDeviceInfo = currentDeviceId ? deviceLookup.get(currentDeviceId) : null;

            setState({
                isOpen: true,
                mode: DEFAULT_MODE,
                count: 1,
                csvFile: null,
                actionUrl: resolvedUrl,
                selectedDevice: sanitizedDeviceId,
                currentDeviceId,
                currentDeviceGroup: currentDeviceInfo?.groupLabel || null,
                isSubmitting: false,
                result: null,
                formError: null,
                itemName: detail.itemName || '',
            });
        };

        window.addEventListener('items:cloneMany', handleOpen);
        return () => {
            window.removeEventListener('items:cloneMany', handleOpen);
        };
    }, [deviceLookup, urlTemplate]);

    const closeModal = useCallback(() => {
        setState((prev) => ({
            ...prev,
            isOpen: false,
            isSubmitting: false,
            result: null,
            formError: null,
        }));
    }, []);

    const handleModeChange = useCallback((event) => {
        const mode = event.target.value === 'csv' ? 'csv' : DEFAULT_MODE;
        setState((prev) => ({
            ...prev,
            mode,
            formError: null,
        }));
    }, []);

    const handleCountChange = useCallback((event) => {
        const value = ensureMinimumCount(event.target.value);
        setState((prev) => ({
            ...prev,
            count: value,
        }));
    }, []);

    const handleDeviceChange = useCallback((event) => {
        const selectedDevice = sanitizeDeviceId(event.target.value);
        const deviceInfo = selectedDevice ? deviceLookup.get(String(selectedDevice)) : null;
        setState((prev) => ({
            ...prev,
            selectedDevice,
            result: null,
            formError: null,
        }));
    }, [deviceLookup]);

    const handleFileChange = useCallback((event) => {
        const file = event.target.files?.[0] ?? null;
        setState((prev) => ({
            ...prev,
            csvFile: file,
            formError: null,
            result: null,
        }));
    }, []);

    const clearFile = useCallback(() => {
        setState((prev) => ({
            ...prev,
            csvFile: null,
            result: null,
        }));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleBrowseClick = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, []);

    const handleSubmit = useCallback(async (event) => {
        if (event && typeof event.preventDefault === 'function') {
            event.preventDefault();
        }

        if (state.isSubmitting) {
            return;
        }

        if (!state.actionUrl) {
            setState((prev) => ({ ...prev, formError: 'Missing clone endpoint.' }));
            return;
        }

        if (state.mode === 'csv' && !state.csvFile) {
            setState((prev) => ({ ...prev, formError: 'Please choose a CSV file to upload.' }));
            return;
        }

        setState((prev) => ({ ...prev, isSubmitting: true, formError: null, result: null }));

        const formData = new FormData();
        formData.append('_token', csrfToken);
        formData.append('mode', state.mode);
        if (state.selectedDevice) {
            formData.append('device', state.selectedDevice);
        }

        if (state.mode === 'csv') {
            formData.append('csv_file', state.csvFile);
        } else {
            formData.append('items', ensureMinimumCount(state.count));
        }

        try {
            const response = await fetch(state.actionUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                },
                body: formData,
            });

            let payload = null;
            try {
                payload = await response.json();
            } catch (parseError) {
                payload = {
                    message: 'Received an unexpected response from the server.',
                    errors: [],
                };
            }

            const resultPayload = {
                message: payload?.message ?? (response.ok ? 'Clone operation completed.' : 'Clone operation failed.'),
                created: payload?.created ?? [],
                errors: payload?.errors ?? [],
                mode: payload?.mode ?? state.mode,
                success: response.ok && (!payload?.errors || payload.errors.length === 0),
            };

            setState((prev) => ({
                ...prev,
                isSubmitting: false,
                result: resultPayload,
            }));

            if (resultPayload.success) {
                window.setTimeout(() => {
                    window.location.reload();
                }, 1500);
            }
        } catch (error) {
            setState((prev) => ({
                ...prev,
                isSubmitting: false,
                formError: 'Unable to reach the server. Please try again.',
            }));
        }
    }, [csrfToken, state]);

    const selectedDeviceValue = state.selectedDevice ?? '';
    const selectedDeviceInfo = selectedDeviceValue ? deviceLookup.get(String(selectedDeviceValue)) : null;
    const selectedDeviceGroup = selectedDeviceInfo?.groupLabel || null;
    const showDeviceWarning = state.currentDeviceGroup && selectedDeviceGroup && state.currentDeviceGroup !== selectedDeviceGroup;

    const resetAndClose = useCallback(() => {
        if (state.isSubmitting) {
            return;
        }
        closeModal();
    }, [closeModal, state.isSubmitting]);

    const renderResultSummary = () => {
        if (!state.result) {
            return null;
        }
        const { message, created, errors, success } = state.result;
        const severity = success ? 'success' : errors.length ? 'warning' : 'info';

        return (
            <Alert severity={severity} sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {message}
                </Typography>
                <Stack spacing={1} divider={<Divider flexItem />}> 
                    {created.length > 0 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Created
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" mt={1}>
                                {created.map((item) => (
                                    <Chip key={item.id || item.name} label={item.name} size="small" />
                                ))}
                            </Stack>
                        </Box>
                    )}
                    {errors.length > 0 && (
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Issues
                            </Typography>
                            <Stack component="ul" spacing={0.5} sx={{ pl: 2, mt: 1 }}>
                                {errors.map((issue, index) => (
                                    <Typography component="li" key={`${issue.row ?? index}-${issue.message}`} variant="body2">
                                        {issue.row ? `Row ${issue.row}: ` : ''}{issue.message}
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </Alert>
        );
    };

    const deviceMenuItems = useMemo(() => {
        const items = [
            <MenuItem key="device-none" value="">
                <em>Use original device</em>
            </MenuItem>,
        ];

        normalizedDevices.forEach((group, groupIndex) => {
            items.push(
                <MenuItem key={`group-${groupIndex}`} value={`__header-${groupIndex}`} disabled>
                    <Typography variant="caption" color="text.secondary">
                        {group.label}
                    </Typography>
                </MenuItem>
            );
            (group.devices || []).forEach((device) => {
                const value = String(device.id);
                items.push(
                    <MenuItem key={`device-${value}`} value={value} sx={{ pl: 3 }}>
                        {device.name}
                    </MenuItem>
                );
            });
        });

        return items;
    }, [normalizedDevices]);

    return (
        <Dialog open={state.isOpen} onClose={resetAndClose} maxWidth="sm" fullWidth>
            <DialogTitle>Clone many</DialogTitle>
            <Box component="form" onSubmit={handleSubmit} noValidate>
                <DialogContent dividers>
                {state.itemName && (
                    <Box mb={3} p={2} borderRadius={2} bgcolor="grey.50" border={1} borderColor="grey.200">
                        <Typography variant="overline" color="text.secondary">
                            Base flux
                        </Typography>
                        <Typography variant="h6" fontWeight={600} color="text.primary">
                            {state.itemName}
                        </Typography>
                    </Box>
                )}

                <FormControl fullWidth margin="normal">
                    <FormLabel id="clone-many-device-label">Select the device</FormLabel>
                    <Select
                        labelId="clone-many-device-label"
                        id="clone-many-device"
                        value={selectedDeviceValue.startsWith('__header-') ? '' : selectedDeviceValue}
                        onChange={handleDeviceChange}
                        disabled={state.isSubmitting}
                    >
                        {deviceMenuItems}
                    </Select>
                    {showDeviceWarning && (
                        <FormHelperText sx={{ color: 'warning.main' }}>
                            You selected a device from a different model group. This may require additional adjustments.
                        </FormHelperText>
                    )}
                </FormControl>

                <FormControl component="fieldset" margin="normal">
                    <FormLabel component="legend">Clone source</FormLabel>
                    <RadioGroup
                        row
                        value={state.mode}
                        onChange={handleModeChange}
                        name="clone-many-mode"
                    >
                        <FormControlLabel
                            value="count"
                            control={<Radio />}
                            label="Number of clones"
                            disabled={state.isSubmitting}
                        />
                        <FormControlLabel
                            value="csv"
                            control={<Radio />}
                            label="Upload CSV"
                            disabled={state.isSubmitting}
                        />
                    </RadioGroup>
                </FormControl>

                {state.mode === 'count' ? (
                    <TextField
                        fullWidth
                        margin="normal"
                        type="number"
                        label="How many clones?"
                        inputProps={{ min: 1, max: 500 }}
                        value={state.count ?? ''}
                        onChange={handleCountChange}
                        disabled={state.isSubmitting}
                        required
                        helperText="Clones will inherit the original name with an auto-incremented suffix."
                    />
                ) : (
                    <Box mt={2}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Upload CSV file
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mt={1} alignItems="center">
                            <TextField
                                fullWidth
                                size="small"
                                value={state.csvFile ? state.csvFile.name : ''}
                                placeholder="Choose CSV file"
                                InputProps={{ readOnly: true }}
                            />
                            <Button
                                variant="outlined"
                                onClick={handleBrowseClick}
                                disabled={state.isSubmitting}
                            >
                                Browse
                            </Button>
                            {state.csvFile && (
                                <Button
                                    variant="text"
                                    color="secondary"
                                    onClick={clearFile}
                                    disabled={state.isSubmitting}
                                >
                                    Remove
                                </Button>
                            )}
                        </Stack>
                        <input
                            type="file"
                            id="clone-many-csv"
                            ref={fileInputRef}
                            hidden
                            accept=".csv,text/csv"
                            onChange={handleFileChange}
                            disabled={state.isSubmitting}
                            required
                        />
                        <FormHelperText>
                            Expected columns: Name, Group, RTSP link. Extra columns will be ignored, missing ones will be skipped.
                        </FormHelperText>
                    </Box>
                )}

                {state.formError && (
                    <Alert severity="error" sx={{ mt: 3 }}>
                        {state.formError}
                    </Alert>
                )}

                {renderResultSummary()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={resetAndClose} disabled={state.isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={state.isSubmitting} variant="contained" color="secondary">
                        {state.isSubmitting ? 'Cloning…' : 'Clone'}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

export default CloneManyModal;
